import {
  Show,
  SimpleShowLayout,
  TextField,
  Labeled,
  SelectField,
  ArrayField,
  Datagrid,
  FunctionField,
  Button,
  useShowController,
  ReferenceField,
} from "react-admin";
import { Grid, useMediaQuery } from "@mui/material";
import BackButtonToolbar from "features/BackButtonToolbar";
import EmployeeField from "server/components/Fields/EmployeeField";
import { ExamCommitteeMemberTypeChoices } from "utils/choices";
import { settings } from "app";
import UpdateStatusDialog from "./UpdateStatusDialog";
import SecuredComponent from "server/security/SecuredComponent";
import permissions from "app/permissions";
import EditIcon from "@mui/icons-material/Edit";
import { useState } from "react";
import UpdateContactForm from "./UpdateContactForm";
import UpdateMemberInfoDialog from "./UpdateMemberInfoDialog";
import DeleteMemberDialog from "./DeleteMemberDialog";
import AddMembersForm from "./AddMembersForm";
import AddIcon from "@mui/icons-material/Add";
import { administrationRequests } from "api/administrationApi";
import { SectionTitle } from "features/SectionTitle";

const ShowExamCommittiee = () => {
  const { record } = useShowController();
  const isMobile = useMediaQuery(theme => theme.breakpoints.down("md"));
  const [editContactMode, setEditContactMode] = useState(false);
  const [addMembersMode, setAddMembersMode] = useState(false);

  return (
    <Show
      title="Детайли на изпитна комисия"
      actions={<BackButtonToolbar />}
    >
      <SimpleShowLayout>
        <SectionTitle
          title="Основна информация"
          textAlign="center"
          sx={{ marginTop: "2rem" }}
        />
        <Grid
          container
          spacing={2}
          sx={isMobile ? settings.style.shadowBoxMobile : settings.style.shadowBox}
        >
          <Grid
            item
            xs={12}
            md={4}
          >
            <Labeled label="Специалност">
              <TextField source="specializationNameBg" />
            </Labeled>
          </Grid>
          <Grid
            item
            xs={12}
            md={4}
          >
            <Labeled label="Име на изпитна комисия">
              <TextField source="name" />
            </Labeled>
          </Grid>
          <Grid
            item
            xs={12}
            md={4}
          >
            <Labeled label="Година">
              <TextField source="year" />
            </Labeled>
          </Grid>
          <Grid
            item
            xs={12}
            md={4}
            sx={{ display: "flex", gap: 2, alignItems: "center" }}
          >
            <Labeled label="Статус">
              <TextField source="statusTextBg" />
            </Labeled>

            <SecuredComponent permission={permissions.canUpdateSpecialistsExamCommitteesStatus}>
              <UpdateStatusDialog
                status={record?.status}
                id={record?.id}
              />
            </SecuredComponent>
          </Grid>
        </Grid>

        <SectionTitle
          title="Членове"
          textAlign="center"
          sx={{ marginTop: "2rem" }}
        />
        <Grid
          container
          spacing={2}
          sx={isMobile ? settings.style.shadowBoxMobile : settings.style.shadowBox}
        >
          <Grid
            item
            xs={12}
          >
            <ArrayField
              source="members"
              label=""
            >
              <Datagrid
                bulkActionButtons={false}
                rowClick={false}
                sx={[settings.style.detailsDatagrid, { "& .RaDatagrid-headerCell": { textAlign: "left" } }]}
              >
                <SelectField
                  source="type"
                  label="Тип"
                  choices={ExamCommitteeMemberTypeChoices}
                  sortable={false}
                />
                <EmployeeField
                  source="employeeId"
                  label="Служител"
                />
                <TextField
                  source="externalEmployeeNames"
                  label="Имена (външни)"
                  sortable={false}
                  emptyText="-"
                />
                <FunctionField
                  render={rec => (
                    <SecuredComponent permission={permissions.canManageSpecialistsExamCommitteesMembers}>
                      <UpdateMemberInfoDialog
                        id={record?.id}
                        memberId={rec?.id}
                        excludeIds={
                          record?.members
                            ?.filter(m => m.id !== rec?.id)
                            ?.map(m => m.employeeId)
                            ?.filter(Boolean) ?? []
                        }
                      />
                    </SecuredComponent>
                  )}
                />
                <FunctionField
                  render={rec => (
                    <SecuredComponent permission={permissions.canManageSpecialistsExamCommitteesMembers}>
                      <DeleteMemberDialog
                        id={record?.id}
                        memberId={rec?.id}
                      />
                    </SecuredComponent>
                  )}
                />
              </Datagrid>
            </ArrayField>
          </Grid>
          <Grid
            item
            xs={12}
          >
            <SecuredComponent permission={permissions.canManageSpecialistsExamCommitteesMembers}>
              {addMembersMode ? (
                <AddMembersForm
                  id={record?.id}
                  excludeIds={record?.members?.map(m => m.employeeId).filter(Boolean) ?? []}
                  setAddMembersMode={setAddMembersMode}
                />
              ) : (
                <Button
                  label="Добави членове"
                  startIcon={<AddIcon />}
                  variant="contained"
                  sx={{ mt: 2 }}
                  onClick={() => setAddMembersMode(true)}
                />
              )}
            </SecuredComponent>
          </Grid>
        </Grid>

        <SectionTitle
          title="Контакт"
          textAlign="center"
          sx={{ marginTop: "2rem" }}
        />
        <Grid
          container
          spacing={2}
          sx={isMobile ? settings.style.shadowBoxMobile : settings.style.shadowBox}
        >
          {editContactMode ? (
            <SecuredComponent permission={permissions.canManageSpecialistsExamCommitteesContact}>
              <UpdateContactForm
                record={record}
                setEditContactMode={setEditContactMode}
              />
            </SecuredComponent>
          ) : (
            <>
              <Grid
                item
                xs={12}
                md={4}
              >
                <Labeled label="Адрес">
                  <TextField source="contact.address" />
                </Labeled>
              </Grid>
              <Grid
                item
                xs={12}
                md={4}
              >
                <Labeled label="Имейл">
                  <TextField source="contact.email" />
                </Labeled>
              </Grid>
              <Grid
                item
                xs={12}
                md={4}
              >
                <Labeled label="Телефон">
                  <TextField source="contact.phoneNumber" />
                </Labeled>
              </Grid>
              <Grid
                item
                xs={12}
                md={4}
              >
                <Labeled label="Мобилен телефон">
                  <TextField source="contact.mobilePhoneNumber" />
                </Labeled>
              </Grid>
              <Grid
                item
                xs={12}
                md={4}
              >
                <Labeled label="Сграда">
                  <ReferenceField
                    source="contact.buildingId"
                    reference={administrationRequests.getBuildingsSelect()}
                    emptyText="-"
                  >
                    <TextField source="name" />
                  </ReferenceField>
                </Labeled>
              </Grid>
              <Grid
                item
                xs={12}
                md={4}
              >
                <Labeled label="Университет">
                  <ReferenceField
                    source="contact.universityId"
                    reference={administrationRequests.getUniversitiesSelect()}
                    emptyText="-"
                  >
                    <TextField source="name" />
                  </ReferenceField>
                </Labeled>
              </Grid>
              <Grid
                item
                xs={12}
              >
                <SecuredComponent permission={permissions.canManageSpecialistsExamCommitteesContact}>
                  <Button
                    label="Редактирай"
                    startIcon={<EditIcon />}
                    variant="contained"
                    onClick={() => setEditContactMode(true)}
                  />
                </SecuredComponent>
              </Grid>
            </>
          )}
        </Grid>
      </SimpleShowLayout>
    </Show>
  );
};

export default ShowExamCommittiee;
